/**
 * Cape Ember Coffee Co. — Central asset registry
 *
 * All production image paths are defined here. Components import from this
 * file so path changes only need to happen in one place.
 *
 * Public-folder assets are served at the root in Create React App, so URLs
 * start with /assets/cape-ember/...
 *
 * IMPORTANT: The Fynbos lifestyle image contains the phrase "From our roastery"
 * as printed text inside the artwork. That phrase must NOT appear in any website
 * copy, alt text, captions or metadata — only the supplied image itself.
 */

const BASE = '/assets/cape-ember';

export const ASSETS = {
  // ── Lifestyle / hero images ───────────────────────────────────────────────
  fynbosLifestyle: {
    src: `${BASE}/cape-ember-fynbos-lifestyle.jpeg`,
    alt: 'Cape Ember Fynbos Roast surrounded by indigenous South African fynbos',
    width: 1080,
    height: 1080,
    link: '/products/fynbos-roast',
  },
  gardenRouteLifestyle: {
    src: `${BASE}/cape-ember-garden-route-lifestyle.jpeg`,
    alt: 'Cape Ember Garden Route Blend overlooking a South African coastal landscape',
    width: 1080,
    height: 1080,
    link: '/products/garden-route-blend',
  },
  emberReserveLifestyle: {
    src: `${BASE}/cape-ember-ember-reserve-lifestyle.jpeg`,
    alt: 'Cape Ember Ember Reserve dark roast inspired by South African mountain valleys',
    width: 1080,
    height: 1080,
    link: '/products/ember-reserve',
  },
  karooHorizonLifestyle: {
    src: `${BASE}/cape-ember-karoo-horizon-lifestyle.jpeg`,
    alt: 'Cape Ember Karoo Horizon coffee inspired by the open Karoo landscape',
    width: 1080,
    height: 1080,
    link: '/products/karoo-horizon',
  },

  // ── Collection / bundle images ────────────────────────────────────────────
  fourProductCollection: {
    src: `${BASE}/cape-ember-four-product-collection.jpeg`,
    alt: 'Cape Ember Coffee Co. collection of four landscape-inspired coffee bags',
    width: 1080,
    height: 1080,
    link: '/shop',
  },
  landscapeBundleBanner: {
    src: `${BASE}/cape-ember-landscape-bundle-banner.jpeg`,
    alt: 'Cape Ember Coffee Co. Landscape Range featuring four South African landscape-inspired coffees',
    width: 1080,
    height: 1080,
    link: '/products/landscape-bundle',
  },
};

/**
 * Ordered list used by the story carousel and any image slider component.
 */
export const LANDSCAPE_CAROUSEL = [
  {
    src: '/assets/cape_ember_01_taste_the_land.jpg',
    alt: 'Cape Ember coffee beside a mountain landscape at sunset',
  },
  {
    src: '/assets/cape_ember_02_garden_route_blend.jpg',
    alt: 'Cape Ember Garden Route Blend coffee bag beside a cup and pastry',
  },
  {
    src: '/assets/cape_ember_03_coffee_moment.jpg',
    alt: 'Woman enjoying a cup of Cape Ember coffee',
  },
  {
    src: '/assets/cape_ember_04_pour_over_collection.jpg',
    alt: 'Cape Ember coffee collection with coffee brewing in a pour-over',
  },
  {
    src: '/assets/cape_ember_05_ember_reserve.jpg',
    alt: 'Cape Ember Ember Reserve coffee bag beside a cup of coffee',
  },
  {
    src: '/assets/cape_ember_06_good_coffee_better_company.jpg',
    alt: 'Friends sharing Cape Ember coffee at a table',
  },
  {
    src: '/assets/cape_ember_07_sedgefield_mosaic.jpg',
    alt: 'Cape Ember Sedgefield Mosaic Roast coffee bag beside a latte',
  },
  {
    src: '/assets/cape_ember_08_slow_sips.jpg',
    alt: 'Coffee being poured beside a Cape Ember coffee bag',
  },
  {
    src: '/assets/cape_ember_09_fynbos_roast.jpg',
    alt: 'Cape Ember Fynbos Roast coffee bag in a coastal landscape',
  },
];

/**
 * Per-product image lookup keyed by product ID.
 */
export const PRODUCT_IMAGES = {
  'fynbos-roast': ASSETS.fynbosLifestyle,
  'garden-route': ASSETS.gardenRouteLifestyle,
  'garden-route-blend': ASSETS.gardenRouteLifestyle,
  'ember-reserve': ASSETS.emberReserveLifestyle,
  'karoo-horizon': ASSETS.karooHorizonLifestyle,
  'landscape-bundle': ASSETS.landscapeBundleBanner,
};

/**
 * Resolve the best available static asset for a product object.
 */
export const getProductAsset = (product) => {
  if (!product) return null;
  return (
    PRODUCT_IMAGES[product.id] ||
    PRODUCT_IMAGES[product.slug] ||
    null
  );
};

/**
 * Build a gallery for PDP and cards when API image arrays are sparse.
 */
export const getProductGallery = (product) => {
  const gallery = [];
  const pushUnique = (entry) => {
    if (!entry?.url) return;
    if (gallery.some((img) => img.url === entry.url)) return;
    gallery.push(entry);
  };

  if (Array.isArray(product?.images)) {
    product.images.forEach((img) => {
      if (!img?.url) return;
      pushUnique({
        url: img.url,
        alt: img.alt || product?.name || 'Cape Ember coffee',
        width: img.width,
        height: img.height,
        is_primary: !!img.is_primary,
      });
    });
  }

  if (product?.image_url) {
    pushUnique({
      url: product.image_url,
      alt: product.name || 'Cape Ember coffee',
      is_primary: gallery.length === 0,
    });
  }

  const primaryAsset = getProductAsset(product);
  if (primaryAsset) {
    pushUnique({
      url: primaryAsset.src,
      alt: primaryAsset.alt,
      width: primaryAsset.width,
      height: primaryAsset.height,
      is_primary: gallery.length === 0,
    });
  }

  if (product?.is_bundle) {
    [ASSETS.landscapeBundleBanner, ASSETS.fourProductCollection].forEach((asset) => {
      pushUnique({
        url: asset.src,
        alt: asset.alt,
        width: asset.width,
        height: asset.height,
      });
    });
  } else {
    pushUnique({
      url: ASSETS.fourProductCollection.src,
      alt: ASSETS.fourProductCollection.alt,
      width: ASSETS.fourProductCollection.width,
      height: ASSETS.fourProductCollection.height,
    });
  }

  if (!gallery.length) {
    pushUnique({
      url: '/placeholder-coffee.jpg',
      alt: product?.name || 'Cape Ember coffee',
      is_primary: true,
    });
  }

  return gallery;
};
